import { useEffect, useState } from "react";
import { ReportTableSection } from "../../Tables/ReportTableSection";
import {
  IActivityObject,
  IBooking,
  ICoPlayerBooking,
  INoShowReport,
  IUser,
  MemberStatisticsExport,
} from "../../../../types/api";
import { createStatisticsFilterStore } from "../../Stores/statisticsFilterStoreFactory";
import { combinedQuery } from "../../../../api/combinedQueries/combinedQueries";
import { toast } from "react-toastify";
import { capitalizeWords } from "../../../../shared/utility";
import createRowsForSpecificMember from "./CreateRowsForSpecificMember";
import "./AdminSpecificMemberModule.scss";

interface AdminSpecificMemberModuleProps {
  weekdayCounts: {
    monday: number;
    tuesday: number;
    wednesday: number;
    thursday: number;
    friday: number;
    saturday: number;
    sunday: number;
  };
  totalDays: number;
  memberOccupancyFilterStore: ReturnType<typeof createStatisticsFilterStore>;
  startDate: string;
  endDate: string;
  toggleSpecificFetch: boolean;
  user: IUser | undefined;
  setShowChooseUserPopup: (show: boolean) => void;
}
const AdminSpecificMemberModule = ({
  weekdayCounts,
  totalDays,
  memberOccupancyFilterStore,
  startDate,
  endDate,
  toggleSpecificFetch,
  user,
  setShowChooseUserPopup,
}: AdminSpecificMemberModuleProps) => {
  const [isFolded, setIsFolded] = useState<boolean>(false);

  const [loading, setLoading] = useState<boolean>(false);
  const [bookings, setBookings] = useState<IBooking[]>([]);
  const [coPlayerBookings, setCoPlayerBookings] = useState<ICoPlayerBooking[]>(
    []
  );
  const [activityBookings, setActivityBookings] = useState<IActivityObject[]>(
    []
  );
  const [noShowReports, setNoShowReports] = useState<INoShowReport[]>([]);
  const [memberOccupancyData, setMemberOccupancyData] =
    useState<MemberStatisticsExport>();

  const [dataFetched, setDataFetched] = useState<boolean>(false);
  const fetchChosenItems = async ({
    fetchBookingsByMember = false,
    fetchCoPlayerBookingsByMember = false,
    fetchActiveActivities = false,
  }) => {
    if (!user) return;
    setDataFetched(false);
    setLoading(true);
    const toastId = toast.loading("Hämtar data för vald medlem...");
    try {
      const response = await combinedQuery({
        requestor: "admin",
        member_id: user?.member_id,
        active: false,
        fetchBookingsByMember: fetchBookingsByMember,
        fetchCoPlayerBookingsByMember: fetchCoPlayerBookingsByMember,
        fetchActiveActivities: fetchActiveActivities,
      });

      if (
        fetchBookingsByMember &&
        response.bookingsByMember &&
        !("message" in response.bookingsByMember)
      ) {
        const bookings = response.bookingsByMember.sort(
          (a: IBooking, b: IBooking) =>
            new Date(b.datetime_start).getTime() -
            new Date(a.datetime_start).getTime()
        );

        setBookings(bookings);
      }
      if (
        fetchCoPlayerBookingsByMember &&
        response.coPlayerBookingsByMember &&
        !("message" in response.coPlayerBookingsByMember)
      ) {
        const bookings = response.coPlayerBookingsByMember.sort(
          (a: ICoPlayerBooking, b: ICoPlayerBooking) =>
            new Date(b.date).getTime() - new Date(a.date).getTime()
        );
        setCoPlayerBookings(bookings);
      }
      if (
        fetchActiveActivities &&
        response.activeActivities &&
        !("message" in response.activeActivities)
      ) {
        const activityBookings = response.activeActivities.sort(
          (a: IActivityObject, b: IActivityObject) =>
            new Date(b.activity.datetime_start).getTime() -
            new Date(a.activity.datetime_start).getTime()
        );

        setActivityBookings(activityBookings);
      }

      setLoading(false);
    } catch (error: any) {
      toast.error(error);
      setLoading(false);
    } finally {
      setDataFetched(true);
      toast.dismiss(toastId);
      toast.success("Data hämtad för vald medlem");
    }
  };

  useEffect(() => {
    if (user) {
      fetchChosenItems({
        fetchBookingsByMember: true,
        fetchCoPlayerBookingsByMember: true,
        fetchActiveActivities: true,
      });
    }
  }, [user, toggleSpecificFetch]);

  useEffect(() => {
    if (dataFetched) {
      const memberOccupancyData = createRowsForSpecificMember(
        memberOccupancyFilterStore,
        bookings,
        coPlayerBookings,
        activityBookings,
        user
          ? capitalizeWords(user.first_name) +
              " " +
              capitalizeWords(user.last_name)
          : "",
        user ? user.member_id : "",
        user ? user.member_type : "",
        startDate,
        endDate,
        weekdayCounts
      );
      setMemberOccupancyData(memberOccupancyData);
      setDataFetched(false);
    }
  }, [dataFetched]);

  return (
    <div className="admin-specific-member-module">
      <div className="minus-margin-wrapper">
        <ReportTableSection
          title="Medlemmar - sök"
          children={
            <div className="specific-member-header-buttons">
              <button
                className="text-m select-style specific-member-choose-user-button"
                onClick={() => setShowChooseUserPopup(true)}
              >
                <span>Välj medlem</span>
              </button>
              <button
                className="select-style"
                onClick={() => setIsFolded(!isFolded)}
              >
                <i
                  className="fa-solid fa-caret-down"
                  style={{
                    transition: "transform 0.2s ease",
                    transform: isFolded ? "rotate(-180deg)" : "rotate(0deg)",
                  }}
                />
              </button>
            </div>
          }
          columns={[
            { key: "name", label: "Namn" },
            { key: "memberType", label: "Medlemstyp" },
            { key: "timespan", label: "Tidsperiod", align: "left" },
            {
              key: "monday",
              label: `Måndag (${weekdayCounts.monday})`,
              align: "left",
            },
            {
              key: "tuesday",
              label: `Tisdag (${weekdayCounts.tuesday})`,
              align: "left",
            },
            {
              key: "wednesday",
              label: `Onsdag (${weekdayCounts.wednesday})`,
              align: "left",
            },
            {
              key: "thursday",
              label: `Torsdag (${weekdayCounts.thursday})`,
              align: "left",
            },
            {
              key: "friday",
              label: `Fredag (${weekdayCounts.friday})`,
              align: "left",
            },
            {
              key: "saturday",
              label: `Lördag (${weekdayCounts.saturday})`,
              align: "left",
            },
            {
              key: "sunday",
              label: `Söndag (${weekdayCounts.sunday})`,
              align: "left",
            },
            {
              key: "total",
              label: `Total (${totalDays})`,
              align: "left",
            },
          ]}
          rows={(memberOccupancyData && memberOccupancyData.data) ?? []}
          useCalcType={false}
          useToggleType={false}
          useStore={memberOccupancyFilterStore}
          isLoading={false}
          folded={isFolded}
        />
      </div>
    </div>
  );
};

export default AdminSpecificMemberModule;
